import { useMemo, useState } from "react";
import { mutate } from "swr";
import { IconShield, IconPlus, IconEdit, IconTrash } from "@tabler/icons-react";
import { ClampText, Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { EmptyState } from "../../../../components/common/empty-state";
import { SearchBar } from "../../../../components/common/input";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import {
  SimpleTable,
  type SimpleTableColumn,
} from "../../../../components/common/simple-table";
import { Dialog } from "../../../../components/common/dialog";
import {
  useMyWarrantyProducts,
  deleteMyWarrantyProduct,
  MY_WARRANTIES_SWR_KEY,
  type WarrantyProductSummary,
} from "../../hooks/useWarrantyProducts";
import { useProductWarranties } from "../../hooks/useProductWarranties";
import { useProductWarrantyTypes } from "../../hooks/useProductWarrantyTypes";
import { useProductWarrantyProviders } from "../../hooks/useProductWarrantyProviders";
import { WarrantyFormDialog } from "../components/warranty-form-dialog";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";

/**
 * Seller-side catalog management for extended-warranty SKUs. Mirrors the
 * /my-products layout: stat cards, search, table — and reuses
 * `WarrantyFormDialog` for create + edit (same dialog used inline on
 * /my-products/new → "Extended warranties").
 */
export function MyWarrantiesPage() {
  const { data, isLoading } = useMyWarrantyProducts();
  const { data: warranties } = useProductWarranties();
  const { data: warrantyTypes } = useProductWarrantyTypes();
  const { data: warrantyProviders } = useProductWarrantyProviders();

  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WarrantyProductSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WarrantyProductSummary | null>(null);

  const periodById = useMemo(
    () => new Map((warranties ?? []).map((w) => [w.id, w.name])),
    [warranties],
  );
  const typeById = useMemo(
    () => new Map((warrantyTypes ?? []).map((t) => [t.id, t.name])),
    [warrantyTypes],
  );
  const providerById = useMemo(
    () => new Map((warrantyProviders ?? []).map((p) => [p.id, p.name])),
    [warrantyProviders],
  );

  const rows = data ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.description, periodById.get(r.warrantyId ?? -1)]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [rows, search, periodById]);

  const columns: SimpleTableColumn<WarrantyProductSummary>[] = [
    {
      key: "name",
      label: "Warranty",
      className: "flex-[2] min-w-0",
      render: (r) => (
        <div className="flex flex-col gap-0.5">
          <ClampText variant="bodyBold" tone="dark" lines={2} title={r.name} className="break-words">
            {r.name}
          </ClampText>
          {r.description && (
            <ClampText
              variant="bodyXs"
              tone="muted"
              lines={2}
              title={r.description}
              className="break-words"
            >
              {r.description}
            </ClampText>
          )}
        </div>
      ),
    },
    {
      key: "price",
      label: "Price (QAR)",
      className: "flex-1",
      render: (r) => (
        <Text variant="body" tone="body">
          {r.price != null ? r.price.toLocaleString() : "—"}
        </Text>
      ),
    },
    {
      key: "period",
      label: "Period",
      className: "flex-1",
      render: (r) => (
        <Text variant="body" tone="body">
          {r.warrantyId != null ? periodById.get(r.warrantyId) ?? "—" : "—"}
        </Text>
      ),
    },
    {
      key: "type",
      label: "Type",
      className: "flex-1",
      render: (r) => (
        <Text variant="body" tone="body">
          {r.warrantyTypeId != null ? typeById.get(r.warrantyTypeId) ?? "—" : "—"}
        </Text>
      ),
    },
    {
      key: "provider",
      label: "Provider",
      className: "flex-1",
      render: (r) => (
        <Text variant="body" tone="body">
          {r.warrantyProviderId != null
            ? providerById.get(r.warrantyProviderId) ?? "—"
            : "—"}
        </Text>
      ),
    },
    {
      key: "usage",
      label: "Used by",
      className: "flex-1",
      render: (r) => {
        const products = r.productUsageCount ?? 0;
        const orders = r.orderUsageCount ?? 0;
        if (products === 0 && orders === 0) {
          return (
            <Text variant="body" tone="muted">
              —
            </Text>
          );
        }
        return (
          <div className="flex flex-col gap-0.5">
            <Text variant="body" tone="dark">
              {products} {products === 1 ? "product" : "products"}
            </Text>
            {orders > 0 && (
              <Text variant="bodyXs" tone="muted">
                {orders} {orders === 1 ? "sale" : "sales"}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "",
      className: "w-24 flex justify-end gap-1",
      render: (r) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Edit warranty"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(r);
              setFormOpen(true);
            }}
            className="inline-flex size-7 items-center justify-center rounded-lg border border-borderDefault bg-bgWhite text-textDark hover:border-textDark"
          >
            <IconEdit size={14} />
          </button>
          <button
            type="button"
            aria-label="Archive warranty"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(r);
            }}
            className="inline-flex size-7 items-center justify-center rounded-lg border border-borderDefault bg-bgWhite text-textDark hover:border-accentError hover:text-accentError"
          >
            <IconTrash size={14} />
          </button>
        </div>
      ),
    },
  ];

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMyWarrantyProduct(deleteTarget.id);
      showSuccessToast("Warranty archived");
      setDeleteTarget(null);
      void mutate(MY_WARRANTIES_SWR_KEY);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
          ?.message;
      const flat = Array.isArray(msg) ? msg.join(", ") : msg;
      showErrorToast(flat || "Failed to archive warranty");
    }
  }

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
      <Heading variant="h2" tone="dark">
        Warranties
      </Heading>

      <div className="flex items-center justify-between">
        <Heading variant="h3" tone="dark">
          All warranties
        </Heading>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<IconPlus size={16} />}
          onClick={openCreate}
          className="!px-5"
        >
          New warranty
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <SearchBar
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, description, or period"
            wrapperClassName=""
          />
        </div>
      </div>

      {isLoading ? (
        <ShimmerLoader message="Fetching your warranties…" lines={6} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={IconShield}
          title="You have no warranty SKUs yet."
          description="Create extended warranties here, then attach them to products from /my-products/new."
          primaryAction={{
            label: "New warranty",
            leftIcon: <IconPlus size={16} />,
            onClick: openCreate,
          }}
          className="rounded-2xl border border-borderDefault bg-bgWhite"
        />
      ) : (
        <SimpleTable
          columns={columns}
          data={filtered}
          keyExtractor={(r) => String(r.id)}
          onRowClick={(r) => {
            setEditing(r);
            setFormOpen(true);
          }}
        />
      )}

      <WarrantyFormDialog
        open={formOpen}
        initial={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => void mutate(MY_WARRANTIES_SWR_KEY)}
      />

      <Dialog
        open={deleteTarget !== null}
        setOpen={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title="Archive warranty?"
        text={
          deleteTarget
            ? buildArchiveText(deleteTarget)
            : ""
        }
        buttonText="Archive"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

function buildArchiveText(w: WarrantyProductSummary): string {
  const products = w.productUsageCount ?? 0;
  const orders = w.orderUsageCount ?? 0;
  const parts: string[] = [
    `"${w.name}" will be hidden from product forms and checkout.`,
  ];
  if (products > 0) {
    parts.push(
      `${products} ${products === 1 ? "product" : "products"} will lose this option.`,
    );
  }
  if (orders > 0) {
    parts.push(
      `${orders} past ${orders === 1 ? "sale is" : "sales are"} preserved with their original price and terms.`,
    );
  } else {
    parts.push("No past sales reference it.");
  }
  return parts.join(" ");
}

export default MyWarrantiesPage;
